import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Theme
} from '@mui/material';
import {
    Science as ScienceIcon,
    Psychology as PsychologyIcon,
    Analytics as AnalyticsIcon,
    PlayArrow as PlayIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { API_ENDPOINTS, apiCall } from '../config/api';

interface Topic {
    id: string;
    name: string;
    description: string;
    charts: string[];
}

export const MachineLearning = () => {
    const [topics, setTopics] = useState<Topic[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTopics();
    }, []);

    const fetchTopics = async () => {
        try {
            const data = await apiCall(API_ENDPOINTS.MACHINE_LEARNING_TOPICS);
            setTopics(data.topics);
        } catch (error) {
            console.error('Error fetching topics:', error);
            // Fallback data if API is not available
            setTopics([
                {
                    id: "supervised_learning",
                    name: "Supervised Learning",
                    description: "Learning with labeled data",
                    charts: ["classification_metrics", "regression_plot", "feature_importance"]
                },
                {
                    id: "unsupervised_learning",
                    name: "Unsupervised Learning",
                    description: "Finding patterns in unlabeled data",
                    charts: ["clustering_plot", "pca_plot", "elbow_method"]
                },
                {
                    id: "deep_learning",
                    name: "Deep Learning",
                    description: "Neural networks and deep architectures",
                    charts: ["training_curves", "confusion_matrix", "feature_maps"]
                },
                {
                    id: "model_evaluation",
                    name: "Model Evaluation",
                    description: "Assessing model performance",
                    charts: ["roc_curve", "precision_recall", "learning_curves"]
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getTopicIcon = (topicId: string) => {
        switch (topicId) {
            case 'supervised_learning':
                return <ScienceIcon />;
            case 'unsupervised_learning':
                return <PsychologyIcon />;
            case 'deep_learning':
                return <ScienceIcon />;
            case 'model_evaluation':
                return <AnalyticsIcon />;
            default:
                return <ScienceIcon />;
        }
    };

    const handleTopicClick = (topicId: string) => {
        setSelectedTopic(selectedTopic === topicId ? null : topicId);
    };

    // SVG Graphics Components
    const MLHero = () => (
        <Box sx={{ position: 'relative', width: '100%', height: { xs: 150, sm: 200, md: 250 }, mb: 4, overflow: 'hidden' }}>
            <svg width="100%" height="100%" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="mlGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fa709a" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#fee140" stopOpacity="0.3" />
                    </linearGradient>
                    <linearGradient id="mlGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#30cfd0" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#330867" stopOpacity="0.3" />
                    </linearGradient>
                </defs>
                {/* Background shapes */}
                <circle cx="150" cy="100" r="70" fill="url(#mlGradient1)" opacity="0.6" />
                <circle cx="650" cy="180" r="90" fill="url(#mlGradient2)" opacity="0.6" />
                <circle cx="400" cy="60" r="50" fill="url(#mlGradient1)" opacity="0.4" />
                
                {/* Neural network nodes */}
                <g transform="translate(200, 120)">
                    <circle cx="0" cy="0" r="12" fill="#fa709a" />
                    <circle cx="40" cy="-20" r="12" fill="#fa709a" />
                    <circle cx="40" cy="20" r="12" fill="#fa709a" />
                    <circle cx="80" cy="-30" r="12" fill="#fa709a" />
                    <circle cx="80" cy="0" r="12" fill="#fa709a" />
                    <circle cx="80" cy="30" r="12" fill="#fa709a" />
                    <line x1="0" y1="0" x2="40" y2="-20" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                    <line x1="0" y1="0" x2="40" y2="20" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                    <line x1="40" y1="-20" x2="80" y2="-30" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                    <line x1="40" y1="-20" x2="80" y2="0" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                    <line x1="40" y1="20" x2="80" y2="0" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                    <line x1="40" y1="20" x2="80" y2="30" stroke="#30cfd0" strokeWidth="2" opacity="0.6" />
                </g>
                
                {/* Clustering visualization */}
                <g transform="translate(500, 100)">
                    <circle cx="0" cy="0" r="8" fill="#fee140" />
                    <circle cx="25" cy="5" r="8" fill="#fee140" />
                    <circle cx="15" cy="20" r="8" fill="#fee140" />
                    <circle cx="80" cy="10" r="8" fill="#30cfd0" />
                    <circle cx="95" cy="25" r="8" fill="#30cfd0" />
                    <circle cx="85" cy="35" r="8" fill="#30cfd0" />
                </g>
                
                {/* Decision tree */}
                <g transform="translate(600, 200)">
                    <rect x="0" y="0" width="30" height="20" fill="#330867" rx="2" />
                    <line x1="15" y1="20" x2="10" y2="40" stroke="#330867" strokeWidth="2" />
                    <line x1="15" y1="20" x2="20" y2="40" stroke="#330867" strokeWidth="2" />
                    <rect x="0" y="40" width="20" height="15" fill="#330867" rx="2" />
                    <rect x="10" y="40" width="20" height="15" fill="#330867" rx="2" />
                </g>
            </svg>
        </Box>
    );

    const MLTopicCardGraphic = ({ topicId }: { topicId: string }) => {
        const graphics: Record<string, JSX.Element> = {
            supervised_learning: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <rect x="20" y="20" width="60" height="60" fill="currentColor" rx="5" />
                        <line x1="20" y1="50" x2="80" y2="50" stroke="white" strokeWidth="3" />
                        <line x1="50" y1="20" x2="50" y2="80" stroke="white" strokeWidth="3" />
                    </svg>
                </Box>
            ),
            unsupervised_learning: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <circle cx="30" cy="30" r="12" fill="currentColor" opacity="0.7" />
                        <circle cx="70" cy="30" r="12" fill="currentColor" opacity="0.7" />
                        <circle cx="50" cy="70" r="12" fill="currentColor" opacity="0.7" />
                        <line x1="30" y1="30" x2="50" y2="70" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="70" y1="30" x2="50" y2="70" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                    </svg>
                </Box>
            ),
            deep_learning: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <circle cx="20" cy="50" r="6" fill="currentColor" />
                        <circle cx="40" cy="30" r="6" fill="currentColor" />
                        <circle cx="40" cy="70" r="6" fill="currentColor" />
                        <circle cx="60" cy="20" r="6" fill="currentColor" />
                        <circle cx="60" cy="50" r="6" fill="currentColor" />
                        <circle cx="60" cy="80" r="6" fill="currentColor" />
                        <circle cx="80" cy="50" r="6" fill="currentColor" />
                        <line x1="20" y1="50" x2="40" y2="30" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="20" y1="50" x2="40" y2="70" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="40" y1="30" x2="60" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="40" y1="30" x2="60" y2="50" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                    </svg>
                </Box>
            ),
            model_evaluation: (
                <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.1, width: 80, height: 80 }}>
                    <svg viewBox="0 0 100 100">
                        <polyline points="10,80 30,60 50,70 70,40 90,20" fill="none" stroke="currentColor" strokeWidth="2" />
                        <circle cx="30" cy="60" r="3" fill="currentColor" />
                        <circle cx="50" cy="70" r="3" fill="currentColor" />
                        <circle cx="70" cy="40" r="3" fill="currentColor" />
                        <circle cx="90" cy="20" r="3" fill="currentColor" />
                    </svg>
                </Box>
            )
        };
        return graphics[topicId] || graphics.supervised_learning;
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Loading Machine Learning Topics...
                </Typography>
            </Container>
        );
    }

    return (
        <Container 
            maxWidth="lg"
            sx={{ 
                px: { xs: 1, sm: 2 }
            }}
        >
            <Box sx={{ position: 'relative', mb: { xs: 3, sm: 4 } }}>
                <MLHero />
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', textAlign: 'center' }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        gutterBottom 
                        align="center" 
                        sx={{ 
                            mb: 1,
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Machine Learning Topics
                    </Typography>
                    
                    <Typography 
                        variant="body1" 
                        paragraph 
                        align="center" 
                        sx={{ 
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            px: { xs: 2, sm: 4 },
                            maxWidth: '600px',
                            margin: '0 auto',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                        }}
                    >
                        Explore various machine learning algorithms and techniques with interactive visualizations and examples.
                    </Typography>
                </Box>
            </Box>

            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    gap: { xs: 2, sm: 3 },
                    flexWrap: 'wrap'
                }}
            >
                {topics.map((topic) => (
                    <Box 
                        key={topic.id} 
                        sx={{ 
                            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.333% - 16px)' }
                        }}
                    >
                        <Card 
                            sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                position: 'relative',
                                overflow: 'hidden',
                                background: (theme: Theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? 'linear-gradient(135deg, rgba(250, 112, 154, 0.1) 0%, rgba(254, 225, 64, 0.1) 100%)'
                                        : 'linear-gradient(135deg, rgba(250, 112, 154, 0.05) 0%, rgba(254, 225, 64, 0.05) 100%)',
                                '&:hover': {
                                    transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                    boxShadow: { xs: 2, sm: 4 },
                                    background: (theme: Theme) => 
                                        theme.palette.mode === 'dark' 
                                            ? 'linear-gradient(135deg, rgba(250, 112, 154, 0.15) 0%, rgba(254, 225, 64, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(250, 112, 154, 0.1) 0%, rgba(254, 225, 64, 0.1) 100%)',
                                },
                                boxShadow: { xs: 1, sm: 2 }
                            }}
                            onClick={() => handleTopicClick(topic.id)}
                        >
                            <MLTopicCardGraphic topicId={topic.id} />
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(250, 112, 154, 0.2)' 
                                                : 'rgba(250, 112, 154, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getTopicIcon(topic.id)}
                                    </Box>
                                    <Typography 
                                        variant='h6' 
                                        component='h2' 
                                        sx={{ 
                                            ml: 1,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            fontWeight: '600'
                                        }}
                                    >
                                        {topic.name}
                                    </Typography>
                                </Box>
                                
                                <Typography 
                                    variant='body2' 
                                    color='text.secondary' 
                                    paragraph
                                    sx={{ 
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        mb: { xs: 2, sm: 3 }
                                    }}
                                >
                                    {topic.description}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography 
                                        variant='subtitle2' 
                                        gutterBottom
                                        sx={{ 
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            mb: { xs: 1, sm: 1.5 }
                                        }}
                                    >
                                        Available Visualizations:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {topic.charts.map((chart) => (
                                            <Chip
                                                key={chart}
                                                label={chart.replace('_', ' ')}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    height: { xs: 20, sm: 24 }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Button
                                    variant="contained"
                                    startIcon={<PlayIcon />}
                                    fullWidth
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.stopPropagation();
                                        // TODO: Implement chart generation
                                        console.log(`Generate visualizations for ${topic.name}`);
                                    }}
                                >
                                    Generate Visualizations
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>
                ))}
            </Box>

            {selectedTopic && (
                <Paper 
                    sx={{ 
                        mt: 4, 
                        p: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        background: (theme: Theme) => 
                            theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(48, 207, 208, 0.1) 0%, rgba(51, 8, 103, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(48, 207, 208, 0.05) 0%, rgba(51, 8, 103, 0.05) 100%)',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, opacity: 0.1 }}>
                        <svg viewBox="0 0 200 200">
                            <polygon points="100,20 140,80 100,140 60,80" fill="currentColor" />
                            <polygon points="100,40 130,80 100,120 70,80" fill="none" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </Box>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {topics.find(t => t.id === selectedTopic)?.name} - Details
                        </Typography>
                        <Typography variant="body1" paragraph>
                            This section will contain detailed information about {selectedTopic} including:
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(250, 112, 154, 0.2)' 
                                                : 'rgba(250, 112, 154, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <ScienceIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Algorithm explanations and theory" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(250, 112, 154, 0.2)' 
                                                : 'rgba(250, 112, 154, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <PsychologyIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Interactive examples and demos" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(250, 112, 154, 0.2)' 
                                                : 'rgba(250, 112, 154, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <TimelineIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Performance metrics and evaluation" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <Box sx={{ 
                                        p: 1, 
                                        borderRadius: '50%', 
                                        background: (theme: Theme) => 
                                            theme.palette.mode === 'dark' 
                                                ? 'rgba(250, 112, 154, 0.2)' 
                                                : 'rgba(250, 112, 154, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <AnalyticsIcon />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText primary="Code implementations and best practices" />
                            </ListItem>
                        </List>
                    </Box>
                </Paper>
            )}
        </Container>
    );
};
